import { Edge, Node } from 'reactflow';
import { WorkflowAnalysis } from './types';

const TRIGGER_ACTIONS = new Set([
  'start', 'webhook',
  'whatsappListener', 'whatsappCloudTrigger', 'metaListenerTrigger',
  'shopifyTrigger', 'odooTrigger', 'easyOrdersTrigger', 'bostaTrigger', 'woocommerceTrigger',
  'sqlTrigger', 'googleDriveTrigger', 'googleCalendarTrigger', 'slackTrigger', 'teamsTrigger', 'sharepointTrigger', 'oneDriveTrigger',
  'typeformTrigger', 'googleFormTrigger', 'outlookTrigger', 'imapTrigger',
]);

export function analyzeWorkflow(nodes: Node[], edges: Edge[]): WorkflowAnalysis {
  const tips: string[] = [];
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();

  for (const n of nodes) {
    outgoing.set(n.id, []);
    incoming.set(n.id, []);
  }
  for (const e of edges) {
    if (!nodeById.has(e.source) || !nodeById.has(e.target)) continue;
    outgoing.get(e.source)!.push(e.target);
    incoming.get(e.target)!.push(e.source);
  }

  const triggers = nodes.filter((n) => TRIGGER_ACTIONS.has(n.data?.action));
  const orphanNodes = nodes
    .filter((n) => {
      if (TRIGGER_ACTIONS.has(n.data?.action)) return false;
      return (incoming.get(n.id)?.length || 0) === 0 && (outgoing.get(n.id)?.length || 0) === 0;
    })
    .map((n) => ({ id: n.id, name: n.data?.name || n.data?.action, action: n.data?.action }));

  const deadEnds = nodes
    .filter((n) => {
      if (TRIGGER_ACTIONS.has(n.data?.action)) return false;
      const outs = outgoing.get(n.id) || [];
      const ins = incoming.get(n.id) || [];
      // Has input but no output, and isn't a natural terminal (output / sender)
      const action = n.data?.action || '';
      const naturalTerminal = /output|send|sender|mail|whatsappSend|response/i.test(action);
      return ins.length > 0 && outs.length === 0 && !naturalTerminal;
    })
    .map((n) => ({ id: n.id, name: n.data?.name || n.data?.action, action: n.data?.action }));

  // Detect simple directed cycles via DFS
  const loops: Array<{ path: string[] }> = [];
  const visited = new Set<string>();
  const stack = new Set<string>();
  const path: string[] = [];

  const dfs = (id: string) => {
    if (stack.has(id)) {
      const idx = path.indexOf(id);
      if (idx >= 0) loops.push({ path: [...path.slice(idx), id] });
      return;
    }
    if (visited.has(id)) return;
    visited.add(id);
    stack.add(id);
    path.push(id);
    for (const next of outgoing.get(id) || []) dfs(next);
    path.pop();
    stack.delete(id);
  };
  for (const n of nodes) dfs(n.id);

  // Connected components (undirected)
  const undirected = new Map<string, Set<string>>();
  for (const n of nodes) undirected.set(n.id, new Set());
  for (const e of edges) {
    undirected.get(e.source)?.add(e.target);
    undirected.get(e.target)?.add(e.source);
  }
  const seen = new Set<string>();
  let components = 0;
  for (const n of nodes) {
    if (seen.has(n.id)) continue;
    components += 1;
    const q = [n.id];
    seen.add(n.id);
    while (q.length) {
      const cur = q.pop()!;
      for (const nxt of undirected.get(cur) || []) {
        if (!seen.has(nxt)) {
          seen.add(nxt);
          q.push(nxt);
        }
      }
    }
  }

  if (!triggers.length) tips.push('No dedicated trigger/Start node — runs will start from the first root node(s) on the canvas.');
  if (orphanNodes.length) tips.push(`${orphanNodes.length} disconnected node(s) are not linked to the flow.`);
  if (deadEnds.length) tips.push(`${deadEnds.length} node(s) stop the flow without a natural terminal action.`);
  if (loops.length) tips.push(`${loops.length} cycle(s) detected — may cause infinite runs.`);
  if (components > 1) tips.push(`Graph has ${components} disconnected parts.`);
  if (nodes.length > 40) tips.push('Large graph — consider splitting into smaller workflows for maintainability.');

  return {
    orphanNodes,
    deadEnds,
    loops: loops.slice(0, 10),
    disconnectedComponents: components || (nodes.length ? 1 : 0),
    triggerCount: triggers.length,
    tips,
  };
}

export function formatAnalysisMarkdown(analysis: WorkflowAnalysis): string {
  const lines = ['**Workflow analysis**', ''];
  lines.push(`- Triggers: ${analysis.triggerCount}`);
  lines.push(`- Disconnected components: ${analysis.disconnectedComponents}`);
  lines.push(`- Orphan nodes: ${analysis.orphanNodes.length}`);
  lines.push(`- Dead ends: ${analysis.deadEnds.length}`);
  lines.push(`- Loops: ${analysis.loops.length}`);
  if (analysis.orphanNodes.length) {
    lines.push('', '**Orphans:**');
    analysis.orphanNodes.slice(0, 12).forEach((n) => lines.push(`- ${n.name} (\`${n.id}\`)`));
  }
  if (analysis.deadEnds.length) {
    lines.push('', '**Dead ends:**');
    analysis.deadEnds.slice(0, 12).forEach((n) => lines.push(`- ${n.name} (\`${n.id}\`)`));
  }
  if (analysis.loops.length) {
    lines.push('', '**Loops:**');
    analysis.loops.slice(0, 5).forEach((l) => lines.push(`- ${l.path.join(' → ')}`));
  }
  if (analysis.tips.length) {
    lines.push('', '**Tips:**');
    analysis.tips.forEach((t) => lines.push(`- ${t}`));
  }
  return lines.join('\n');
}
